import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { XIcon } from "lucide-react";

import { RemoteSelect } from "@/components/fields/remote-select";
import { Button } from "@/components/ui/button";
import { useSportsRepository } from "@/pages/sports/hooks/use-sports-repository";

/** Narrows the match list to one league through the `/matches?leagueId=` URL contract. */
export function LeagueFilter() {
  const { searchLeagues, getLeague } = useSportsRepository();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const leagueId = params.get("leagueId");
  const [selected, setSelected] = useState<any>();

  // Name the league behind a deep-linked `?leagueId=`.
  useEffect(() => {
    if (!leagueId) {
      setSelected(undefined);
      return;
    }
    if (selected?.id === leagueId) return;
    let cancelled = false;
    getLeague(leagueId)
      .then((league: any) => {
        if (!cancelled) setSelected({ ...league, id: leagueId });
      })
      .catch(() => {
        if (!cancelled) setSelected({ id: leagueId, name: leagueId });
      });
    return () => {
      cancelled = true;
    };
  }, [leagueId]);

  const go = (next: URLSearchParams) => {
    const qs = next.toString();
    navigate(qs ? `/matches?${qs}` : "/matches");
  };

  return (
    <div className="flex items-end gap-2">
      <RemoteSelect<any>
        label="League"
        className="w-[300px]"
        value={selected}
        accessorFn={(league) =>
          league?.id
            ? {
                id: league.id,
                name: [league.name, league.country].filter(Boolean).join(" · "),
              }
            : undefined
        }
        onSearch={(query) => searchLeagues(query)}
        onValueChanged={(league) => {
          if (!league?.id) return;
          setSelected(league);
          const next = new URLSearchParams(params);
          next.set("leagueId", league.id);
          go(next);
        }}
      />
      {leagueId && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            const next = new URLSearchParams(params);
            next.delete("leagueId");
            go(next);
          }}
        >
          <XIcon className="size-4" />
          Clear league
        </Button>
      )}
    </div>
  );
}
